// Storefront cart API over the aldoo-ra Store (multi-item cart, persisted by
// the framework under "aldoo-store-cart"). Lines are framework items built by
// checkout/cartItems.toCartItem: { pid, name, amount, currency, quantity,
// options: { platform, licenses, seller }, product }, plus the buyer's
// selection ({ sku, choices, choiceLabels, components, lineKey }) when the
// product page had one.
import Value, { getValue, setValue } from "aldoo-ra/Value"
import { store } from "aldoo-ra/Store"
import { showSuccessToast } from "aldoo-ra/Toast"
import { makeT, getTable } from "../hooks/useT"
import { toCartItem, itemUnitPrice, gaItem, lineKey, hasPlatformSelection } from "../checkout/cartItems"
import { selectionOf } from "aldoo-ra/Store/lines"
import { ListProducts, LIST_FIELDS } from "../api"
import { isPhysicalProduct } from "../utils/productUtils"
import { PLACEMENTS } from "./placements"
import { scheduleStockCheck } from "./stockCheck"
import { track } from "../analytics"

export { itemUnitPrice, gaItem }

const CART_KEY = "aldoo-store-cart"
export const CART_BOUNCE = "aldoo-cart-bounce"

export function useCart() {
  const [cart] = Value({ key: CART_KEY, defaultValue: [], persistKey: CART_KEY })
  return cart || []
}

export const cartCount = (cart) => (cart || []).reduce((n, line) => n + (line.quantity || 0), 0)

// every line of the product: two sizes of one product are two lines
export const cartQuantityOf = (cart, pid) => (cart || []).reduce((n, line) => (line.pid === pid ? n + (line.quantity || 0) : n), 0)

// the product's plain line (its key is the pid): a line with a selection is
// reached by its own key
export const cartLineOf = (cart, pid) => (cart || []).find((line) => lineKey(line) === pid) || null

const bounce = () => setValue(CART_BOUNCE, (getValue(CART_BOUNCE) || 0) + 1)

/**
 * "Added to cart". Every buy button ends up here, so the confirmation is said
 * once, for all of them. It is outside React (t() is a hook), so the table of
 * the current site language is read directly.
 *
 * Dismissal tracking is off: the toast is the answer to an action, not a
 * notice, and dismissing one must not silence the next.
 */
function addedToast(line, quantity) {
  const t = makeT(getTable(getValue("current-site-language") || "en"))
  const name = line?.name || line?.product?.name
  showSuccessToast(t`Added to cart`, quantity > 1 && name ? `${name} × ${quantity}` : name || "", {
    duration: 2500,
    enableDismissalTracking: false,
  })
}

/**
 * Adds a product (merged by line key: the pid, or the pid and the buyer's
 * selection). `placement` says where on the store front it was added from
 * (store/placements), for the add-to-cart statistics. `options.platform` is the license distribution
 * [{ platformId, quantity }] of license products; `selection` is the product
 * page's selection line (aldoo-ra/Store/extensions useProductSelection).
 */
export function addToCart(product, quantity = 1, options = {}, { proceedToCart = false, selection = null, placement = "other" } = {}) {
  const item = toCartItem(product, quantity, options, selection)
  // where it was added from (store/placements): the admin's dashboards count it
  track("ADD_TO_CART", { pid: product?.pid, placement, quantity, kind: "add", ...(item?.sku ? { sku: item.sku } : {}) })
  const line = store.addToCart({ product: item, quantity, options, proceedToCart })
  bounce()
  // the server says in a moment whether there is that much
  scheduleStockCheck([lineKey(line || item)])
  // the cart opening is its own confirmation
  if (!proceedToCart) addedToast(line || item, quantity)
  return line
}

/** Takes a line out of the cart (the card's stepper at one, the cart's bin); a plain line's key is its pid. */
export function removeFromCart(key) {
  store.removeItem(key)
  scheduleStockCheck()
}

/** Replaces a line's quantity and options (license dialog "Update"); a plain line's key is its pid. */
export function updateCartLine(key, quantity, options, placement = null) {
  // one more of a line (a stepper's "+", a licence count raised) is an add from where it was pressed
  const before = (getValue(CART_KEY) || []).find((line) => lineKey(line) === key)
  if (placement && before && quantity > (before.quantity || 0)) track("ADD_TO_CART", { pid: before.pid, placement, quantity: quantity - (before.quantity || 0), kind: "increase", ...(before.sku ? { sku: before.sku } : {}) })
  store.setLine(key, { quantity, options })
  scheduleStockCheck([key])
}

export const openCart = () => store.showCart()

/**
 * Several products at once (the series banner's "Get now"): each one line
 * of one, said once for all — the cart opens, which is the confirmation —
 * and counted at `placement`. Answers how many went in.
 */
export function addProductsToCart(products, placement = "other") {
  const added = []
  for (const product of products || []) {
    if (!product?.pid) continue
    const item = toCartItem(product, 1, {}, null)
    track("ADD_TO_CART", { pid: product.pid, placement, quantity: 1, kind: "add" })
    store.addToCart({ product: item, quantity: 1, options: {} })
    added.push(lineKey(item))
  }
  if (!added.length) return 0
  bounce()
  scheduleStockCheck(added)
  store.showCart()
  return added.length
}

/**
 * An earlier order back in the cart ("Order again"): its products as the store sells them now (today's
 * price), each line with what was chosen on it (a size, an outfit's parts), on top of what the cart
 * holds; then the cart opens. Left out: what the store no longer sells, a download the buyer owns
 * already, and a license key (its platform is chosen on its page). Answers how many lines went in.
 */
export async function addOrderToCart(lines, { language, currency } = {}) {
  const wanted = (lines || []).filter((line) => line?.pid && line.kind !== "shipping")
  if (!wanted.length) return 0
  const pids = [...new Set(wanted.map((line) => line.pid))]
  const res = await ListProducts({ page: 0, pids, items_per_page: pids.length, language, currency, owned_only: false, fields: LIST_FIELDS }).catch(() => null)
  const byPid = new Map((res?.list || []).map((product) => [product.pid, product]))
  const added = []
  for (const line of wanted) {
    const product = byPid.get(line.pid)
    if (!product || hasPlatformSelection(product)) continue
    if (product.is_bought && !isPhysicalProduct(product)) continue
    const quantity = Math.max(1, Math.min(99, line.quantity || 1))
    const selection = selectionOf(line)
    const item = toCartItem(product, quantity, {}, Object.keys(selection).length ? selection : null)
    track("ADD_TO_CART", { pid: product.pid, placement: PLACEMENTS.REORDER, quantity, kind: "add", ...(item?.sku ? { sku: item.sku } : {}) })
    store.addToCart({ product: item, quantity, options: {} })
    added.push(lineKey(item))
  }
  if (!added.length) return 0
  bounce()
  scheduleStockCheck(added)
  store.showCart()
  return added.length
}
